import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import { BrowserRouter } from 'react-router-dom';
import { showWarningToast } from '@/components/ui/CustomToast';
import { logger } from '@/lib/logger';
import { recordApi403 } from '@/lib/rbac-monitor';
import { getSessionToken, formatSessionTokenHeader } from '@/lib/session-auth';
import { useApiDebugStore } from '@/stores/apiDebugStore';
import { isTokenExpired, refreshSession, getAccessToken } from '@/utils/auth';
import { initializeConsoleInterceptor } from '@/utils/consoleInterceptor';
import { debug } from '@/utils/debug';
import { isDebugPanelEnabled, sanitizeHeaders } from '@/utils/debugUtils';
import { initializeTheme } from '@/utils/initializeTheme';
import { reloadOnceForChunkError } from '@/utils/lazyRetry';
import { installStaffPlatformChatAuth, isStaffPlatformChatRequest } from '@/utils/platformChatAuth';
import { parseRetryAfter } from '@/utils/retryAfter';
import { initializeSentry } from '@/utils/sentry';
import App from './App';
import './App.css';
import reportWebVitals from './reportWebVitals';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import versionData from './version.json';

initializeSentry({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  release: `superclinic-frontend@${versionData.version}`,
  isDev: import.meta.env.DEV,
});

// Recover from stale-deploy chunk failures: Vite fires `vite:preloadError`
// whenever a dynamically imported module (or its preloaded deps) fails to
// load — typically because a new deploy replaced the hashed chunk files.
// A single guarded reload fetches the fresh index.html and assets instead
// of leaving the user on a blank/broken page.
window.addEventListener('vite:preloadError', (event) => {
  if (reloadOnceForChunkError()) {
    event.preventDefault();
  }
});

// Initialize theme before rendering
initializeTheme();

// Initialize console interceptor for debug panel (only in development)
if (isDebugPanelEnabled()) {
  initializeConsoleInterceptor();
}

// Get the API URL from environment variables
const apiBaseUrl = import.meta.env.VITE_API_URL;

// Set the base URL for axios
if (apiBaseUrl) {
  // If we have an explicit API URL from env vars, use it
  // Check if the API URL already ends with /api/v1
  if (apiBaseUrl.endsWith('/api/v1') || apiBaseUrl.endsWith('/api/v1/')) {
    axios.defaults.baseURL = apiBaseUrl.replace(/\/$/, '');
  } else {
    axios.defaults.baseURL = (apiBaseUrl + '/api/v1').replace(/\/$/, '');
  }
} else {
  // In development with Vite proxy, use relative path
  axios.defaults.baseURL = '/api/v1';
  logger.info('Using relative API path with proxy: /api/v1');
}

// Set security header for issue report requests
// This helps prevent unauthorized API access from bots or external sources
const issueReportClientSecret = import.meta.env.VITE_ISSUE_REPORT_CLIENT_SECRET;
if (issueReportClientSecret) {
  axios.defaults.headers.common['X-App-Client-Key'] = issueReportClientSecret;
}

// Add request interceptor
axios.interceptors.request.use(
  async (config) => {
    // DEBUG: Log every single request
    debug.log('[Interceptor] Processing request:', {
      url: config.url,
      method: config.method,
      hasAuthHeader: !!config.headers?.Authorization,
      hasSessionHeader: !!config.headers?.['X-Session-Token'],
    });

    // Track API call in debug store (only if debug panel is enabled)
    if (isDebugPanelEnabled()) {
      const debugStore = useApiDebugStore.getState();
      if (debugStore.isEnabled) {
        // Construct the full URL including base URL
        const fullUrl = (() => {
          const url = config.url || '';
          // If it's already a full URL, use it as-is
          if (url.startsWith('http://') || url.startsWith('https://')) {
            return url;
          }
          // Otherwise, combine baseURL with the path
          const baseURL = config.baseURL || axios.defaults.baseURL || '';
          // Handle relative URLs properly
          if (baseURL && !url.startsWith('/') && !baseURL.endsWith('/')) {
            return `${baseURL}/${url}`;
          }
          return `${baseURL}${url}`;
        })();

        const callId = debugStore.addCall({
          url: fullUrl,
          method: config.method?.toUpperCase() || 'GET',
          requestHeaders: config.headers,
          requestData: config.data,
          startTime: Date.now(),
        });
        // Attach the call ID to the config for later reference
        config._debugCallId = callId;
      }
    }

    // Log user-related API calls
    if (config.url?.includes('/users/')) {
      logger.debug('User API Request', {
        url: config.url,
        method: config.method?.toUpperCase(),
        headers: sanitizeHeaders(config.headers as Record<string, any>),
        data: config.data,
      });
    }

    // Remove trailing slashes and add version-based cache buster
    // This ensures all API calls match backend's trailing_slash=False configuration
    // The version param busts browser-cached 301 redirects on new deployments
    if (config.url) {
      const originalUrl = config.url;
      const [path, query] = config.url.split('?');
      const cleanPath = path.replace(/\/+$/, '');
      const versionParam = `_v=${versionData.version}`;
      config.url = cleanPath + (query ? `?${query}&${versionParam}` : `?${versionParam}`);
      debug.log('[Interceptor] URL transformed:', {
        original: originalUrl,
        transformed: config.url,
      });
    }

    // Public endpoints that should NOT have auth headers (truly anonymous endpoints)
    const PUBLIC_ENDPOINTS = [
      '/auth/registration/otp/send', // Anonymous - anyone can request OTP (NEW endpoint)
      '/auth/registration/otp/verify', // Anonymous - verify OTP to GET session token (NEW endpoint)
      '/auth/registration/check-email', // Anonymous - pre-signup email availability check
      '/auth/otp/send', // Legacy endpoint (for backward compatibility)
      '/auth/otp/verify', // Legacy endpoint (for backward compatibility)
      '/registration/partner/pincode-availability', // Anonymous - check pincode
      '/registration/checkout-support', // Anonymous - support requests
      // Note: /registration/discount-code/verify requires session token (after OTP)
    ];

    // Check if this is a public endpoint (handle both with and without trailing slashes)
    const isPublicEndpoint = PUBLIC_ENDPOINTS.some((endpoint) => {
      const url = config.url || '';
      // Normalize by removing trailing slash for comparison
      const normalizedUrl = url.replace(/\/$/, '');
      const normalizedEndpoint = endpoint.replace(/\/$/, '');
      return normalizedUrl.includes(normalizedEndpoint);
    });

    // Endpoints where the pre-registration session token IS the credential, so
    // it still wins over any Cognito JWT the browser happens to hold.
    const PRE_REGISTRATION_ENDPOINTS = [
      'auth/registration/',
      'auth/otp/',
      'registration/checkout',
      'registration/discount-code/verify',
      'registration/partner/',
    ];

    const isPreRegistrationEndpoint = PRE_REGISTRATION_ENDPOINTS.some((endpoint) =>
      (config.url || '').includes(endpoint)
    );

    if (isPublicEndpoint) {
      debug.log('[Interceptor] ✅ Public endpoint detected, skipping auth:', config.url);
      debug.log('[Interceptor] Headers will NOT be added for this request');
    } else {
      debug.log('[Interceptor] 🔒 Protected endpoint detected:', config.url);
    }

    // Only try to add Authorization header if not present AND not a public endpoint
    if (!config.headers.Authorization && !config.headers['X-Session-Token'] && !isPublicEndpoint) {
      debug.log('[Interceptor] No auth headers found, attempting to add...');
      try {
        // The session token is a PRE-REGISTRATION credential: the API
        // authenticates it as a PreRegistrationIdentity, which owns no
        // UserProfile and therefore no role, so every RBAC-guarded endpoint
        // answers 403. It lives in a 24h cookie that nothing clears once the
        // member finishes checkout and signs in, so preferring it
        // unconditionally made a signed-in member 403 on their own data
        // (e.g. PUT /users/users/{id}/avatar) while endpoints that pass an
        // explicit Bearer token kept working. Prefer the Cognito JWT, and fall
        // back to the session token only where it is actually the credential:
        // the pre-registration endpoints, or when there is no Cognito session.
        const sessionToken = getSessionToken();

        const applySessionToken = (token: string) => {
          config.headers.Authorization = formatSessionTokenHeader(token);
          config.headers['X-Session-Token'] = token;
          logger.debug('[Auth] Using session token for request', {
            url: config.url,
            method: config.method?.toUpperCase(),
          });
        };

        if (sessionToken && isPreRegistrationEndpoint) {
          applySessionToken(sessionToken);
        } else {
          let hasJWT = false;

          try {
            const session = await fetchAuthSession();
            const token = getAccessToken(session);

            // Proactive token expiration check
            if (token && isTokenExpired(token)) {
              debug.log('[Auth] Token expired, refreshing before request...');
              const newSession = await refreshSession();
              const newToken = getAccessToken(newSession);
              if (newToken) {
                config.headers.Authorization = `Bearer ${newToken}`;
                hasJWT = true;
                logger.debug('[Auth] Using refreshed JWT token for request', {
                  url: config.url,
                  method: config.method?.toUpperCase(),
                });
              }
            } else if (token) {
              config.headers.Authorization = `Bearer ${token}`;
              hasJWT = true;
              logger.debug('[Auth] Using JWT token for request', {
                url: config.url,
                method: config.method?.toUpperCase(),
              });
            }
          } catch (amplifyError) {
            debug.error('[Auth] Amplify session fetch failed:', amplifyError);
          }

          // Not signed in yet — the rest of the checkout flow (profile reads,
          // discount codes) still authenticates with the session token.
          if (!hasJWT && sessionToken) {
            applySessionToken(sessionToken);
          }
        }
      } catch (error) {
        debug.error('Error adding authorization header:', error);
      }
    } else {
      debug.log('[Interceptor] Auth headers already present, skipping:', {
        hasAuthHeader: !!config.headers.Authorization,
        hasSessionHeader: !!config.headers['X-Session-Token'],
      });
      // Check if there's an Authorization header and ensure it's properly formatted
      const authHeader = config.headers.Authorization as string;
      if (authHeader && !authHeader.startsWith('Bearer ')) {
        // Handle case where token is passed directly without 'Bearer ' prefix
        config.headers.Authorization = `Bearer ${authHeader}`;
      }

      // Clean up the token if it has toString() artifacts from a corrupted or
      // deserialized session. A plain fetchAuthSession() can hand back the same
      // bad cached session, so force a refresh. If we still can't get a clean
      // token, drop the malformed header rather than send "[object Object]" —
      // sending garbage produces the silent auth failures behind reports that
      // only clear after logging in via incognito (a fresh cookie store).
      if (authHeader && authHeader.includes('[object Object]')) {
        try {
          const session = await refreshSession();
          const token = getAccessToken(session);
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          } else {
            delete config.headers.Authorization;
          }
        } catch (error) {
          debug.error('Error fixing authorization header:', error);
          delete config.headers.Authorization;
        }
      }
    }

    return config;
  },
  (error) => {
    debug.error('Request error:', error);
    // Track request error in debug store (only if debug panel is enabled)
    if (isDebugPanelEnabled()) {
      const debugStore = useApiDebugStore.getState();
      if (debugStore.isEnabled && error.config) {
        const callId = error.config._debugCallId;
        if (callId) {
          debugStore.updateCall(callId, {
            error: error.message,
            endTime: Date.now(),
          });
        }
      }
    }
    return Promise.reject(error);
  }
);

installStaffPlatformChatAuth(axios, apiBaseUrl);

// Flag to prevent multiple redirects
let isRedirecting = false;

// Timestamp that throttles 429 toasts so bursts only surface a single message.
let lastRateLimitToastAt = 0;
const RATE_LIMIT_TOAST_COOLDOWN_MS = 5000;

const showRateLimitToast = (retryAfterSeconds: number | null) => {
  const now = Date.now();
  if (now - lastRateLimitToastAt < RATE_LIMIT_TOAST_COOLDOWN_MS) {
    return;
  }
  lastRateLimitToastAt = now;

  const retryMessage =
    retryAfterSeconds && retryAfterSeconds > 0
      ? `Too many requests. Please try again in ${retryAfterSeconds} second${retryAfterSeconds === 1 ? '' : 's'}.`
      : 'Too many requests. Please try again in a moment.';

  try {
    showWarningToast('Slow down', retryMessage);
  } catch (toastError) {
    debug.warn('Failed to show rate-limit toast:', toastError);
  }
};

// Add response interceptor
axios.interceptors.response.use(
  (response) => {
    // Track successful response in debug store (only if debug panel is enabled)
    if (isDebugPanelEnabled()) {
      const debugStore = useApiDebugStore.getState();
      if (debugStore.isEnabled && response.config) {
        const callId = response.config._debugCallId;
        if (callId) {
          debugStore.updateCall(callId, {
            status: response.status,
            statusText: response.statusText,
            responseData: response.data,
            endTime: Date.now(),
          });
        }
      }
    }

    // Log user-related API responses
    if (response.config.url?.includes('/users/')) {
      debug.group('✅ User API Response');
      debug.log('URL:', response.config.url);
      debug.log('Status:', response.status);
      debug.log('Response Data:', response.data);
      debug.groupEnd();
    }
    return response;
  },
  async (error) => {
    // Track error response in debug store (only if debug panel is enabled)
    if (isDebugPanelEnabled()) {
      const debugStore = useApiDebugStore.getState();
      if (debugStore.isEnabled && error.config) {
        const callId = error.config._debugCallId;
        if (callId) {
          debugStore.updateCall(callId, {
            status: error.response?.status,
            statusText: error.response?.statusText,
            responseData: error.response?.data,
            error: {
              message: error.message,
              code: error.code,
              stack: error.stack,
            },
            endTime: Date.now(),
          });
        }
      }
    }

    // Log user-related API errors
    if (error.config?.url?.includes('/users/')) {
      debug.group('❌ User API Error');
      debug.log('URL:', error.config.url);
      debug.log('Status:', error.response?.status);
      debug.log('Error Data:', error.response?.data);
      debug.log('Error Message:', error.message);
      debug.groupEnd();
    }

    // Store the original request to retry it later
    const originalRequest = error.config;

    if (error.code === 'ERR_NETWORK') {
      debug.error('Network error details:', {
        message: error.message,
        config: error.config,
        baseURL: axios.defaults.baseURL,
      });
    } else if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      debug.error('Response error:', {
        data: error.response.data,
        status: error.response.status,
        headers: error.response.headers,
      });

      // Handle rate limiting (DRF sets Retry-After in seconds when throttling)
      if (error.response.status === 429) {
        const retryAfterSeconds = parseRetryAfter(error.response.headers?.['retry-after']);

        // Attach for downstream consumers (withRetry, feature error handlers).
        if (originalRequest) {
          originalRequest._retryAfterSeconds = retryAfterSeconds ?? undefined;
        }

        showRateLimitToast(retryAfterSeconds);
        return Promise.reject(error);
      }

      // Handle authorization errors
      if (error.response.status === 401 || error.response.status === 403) {
        if (isStaffPlatformChatRequest(originalRequest, apiBaseUrl)) return Promise.reject(error);
        const errorMessage = error.response.data?.detail || error.response.data?.error || '';
        const isTokenExpiredError =
          errorMessage.toLowerCase().includes('token has expired') ||
          errorMessage.toLowerCase().includes('token expired') ||
          errorMessage.toLowerCase().includes('invalid token') ||
          errorMessage.toLowerCase().includes('token is invalid');

        debug.error('Authorization error:', {
          status: error.response.status,
          message: errorMessage,
          isTokenExpired: isTokenExpiredError,
        });

        // Check if this endpoint should NOT trigger redirect on 401
        const requestUrl = originalRequest?.url || '';
        const NO_REDIRECT_ENDPOINTS = [
          '/marketplace/catalog', // Catalog browsing - component handles errors gracefully
        ];
        const shouldSkipRedirect = NO_REDIRECT_ENDPOINTS.some((endpoint) =>
          requestUrl.includes(endpoint)
        );

        if (shouldSkipRedirect) {
          debug.log('[Auth] Skipping redirect for endpoint:', requestUrl);
          return Promise.reject(error);
        }

        // Only attempt token refresh for token expiration errors
        // For other 403s (permission denied), just let the error propagate
        if (isTokenExpiredError || error.response.status === 401) {
          // Initialize retry counter if not exists
          if (!originalRequest._retryCount) {
            originalRequest._retryCount = 0;
          }

          // Allow up to 3 retries for token expiration errors
          const MAX_RETRIES = 3;
          if (originalRequest._retryCount < MAX_RETRIES) {
            originalRequest._retryCount++;
            debug.log(`Token refresh attempt ${originalRequest._retryCount}/${MAX_RETRIES}...`);

            try {
              // Wait a bit before retrying (100ms * retry count)
              await new Promise((resolve) =>
                setTimeout(resolve, 100 * originalRequest._retryCount)
              );

              // Try to refresh the session
              const newSession = await refreshSession();
              const newToken = getAccessToken(newSession);

              if (newToken) {
                debug.log(`Token refreshed successfully (attempt ${originalRequest._retryCount})`);
                // Update the authorization header with the fresh token
                originalRequest.headers.Authorization = `Bearer ${newToken}`;

                // Retry the original request with the new token
                return axios(originalRequest);
              } else {
                // If no token from refresh, try direct fetch
                const session = await fetchAuthSession({ forceRefresh: true });
                const token = getAccessToken(session);

                if (token) {
                  debug.log(`Token fetched successfully (attempt ${originalRequest._retryCount})`);
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                  return axios(originalRequest);
                }
              }
            } catch (refreshError) {
              debug.error(
                `Token refresh failed (attempt ${originalRequest._retryCount}):`,
                refreshError
              );

              // If we haven't reached max retries, let the error bubble up to retry
              if (originalRequest._retryCount < MAX_RETRIES) {
                // Continue to next retry
                return Promise.reject(error);
              }

              // Max retries reached - redirect to login only if not already redirecting
              debug.error('Max token refresh attempts reached.');
              if (!isRedirecting && !window.location.pathname.includes('/login')) {
                isRedirecting = true;
                debug.error('Redirecting to login...');
                window.location.href = '/login';
              }
            }
          } else {
            // Max retries reached
            debug.error(`Max token refresh retries (${MAX_RETRIES}) exceeded.`);
            if (!isRedirecting && !window.location.pathname.includes('/login')) {
              isRedirecting = true;
              debug.error('Redirecting to login...');
              window.location.href = '/login';
            }
          }
        } else {
          // This is a permission error (403), not an auth error
          // Let it propagate to the caller to handle
          const method = (originalRequest?.method ?? 'GET').toUpperCase();
          const path = originalRequest?.url ?? 'unknown';
          recordApi403(path, method);
          debug.log('Permission denied (403) - not attempting token refresh');
        }
      }
    } else if (error.request) {
      // The request was made but no response was received
      debug.error('No response received:', error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      debug.error('Error setting up request:', error.message);
    }
    return Promise.reject(error);
  }
);

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </React.StrictMode>
);

// Send Core Web Vitals field data to PostHog (captured only once the PostHog
// provider has initialised; metrics that fire earlier are dropped silently).
reportWebVitals((metric) => {
  import('posthog-js').then(({ default: posthog }) => {
    if (posthog.__loaded) {
      posthog.capture('web_vitals', {
        metric_name: metric.name,
        metric_value: metric.value,
        metric_delta: metric.delta,
        metric_id: metric.id,
      });
    }
  });
});
